import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { ParticipantType } from '@vendasta/conversation';
import { CardDataType, GalaxyContactInfoCardModule } from '@vendasta/galaxy/contact-info-card';
import { combineLatest, map } from 'rxjs';
import { ConversationService } from '../../../../../../../core/src/lib/conversation.service';
import { PARTNER_ID_TOKEN } from '../../../../../../../core/src/lib/tokens';

const detailsParticipantTypes = [
  ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
  ParticipantType.PARTICIPANT_TYPE_PARTNER,
  ParticipantType.PARTICIPANT_TYPE_VENDOR,
];

@Component({
  selector: 'inbox-conversation-details',
  standalone: true,
  imports: [CommonModule, MatToolbarModule, MatIconModule, TranslateModule, GalaxyContactInfoCardModule],
  templateUrl: './conversation-details.component.html',
  styleUrls: ['./conversation-details.component.scss'],
})
export default class ConversationDetailsComponent {
  private conversationService = inject(ConversationService);
  private conversationDetail$ = this.conversationService.currentConversationDetail$;

  @Input() showTickets = false;

  @Output() ticketsClicked = new EventEmitter<void>();

  private participants$ = this.conversationDetail$.pipe(map((conversations) => conversations?.participants || []));

  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  readonly cardData$ = combineLatest([this.participants$, this.partnerId$]).pipe(
    map(([participants, currentPartnerId]) =>
      participants
        .filter((participant) => !!participant)
        .filter(
          (participant) =>
            participant?.isSubjectParticipant || detailsParticipantTypes.includes(participant.participantType),
        )
        .filter(
          (participant) =>
            !(
              participant.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER &&
              participant.partnerId === currentPartnerId
            ),
        )
        .map((participant) => {
          const {
            name,
            participantType,
            email,
            phoneNumber,
            profileImageUrl,
            partnerId,
            accountGroupId,
            internalParticipantId,
          } = participant;
          const { firstLineAddress, secondLineAddress, city, state, country, zipCode } = participant.address || {};
          const cardType = participantType === ParticipantType.PARTICIPANT_TYPE_IAM_USER ? 'user' : 'business';

          return {
            type: cardType,
            id: internalParticipantId || accountGroupId || partnerId,
            email: email,
            phone: phoneNumber,
            avatar: profileImageUrl,
            businessName: name,
            businessAddress: {
              address1: firstLineAddress,
              address2: secondLineAddress,
              city: city,
              state: state,
              country: country,
              zip: zipCode,
            },
          } as CardDataType;
        }),
    ),
  );
}
